import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PRIMARY, cn } from '../constants/theme';
import { PageProps } from '../types';
import { Header } from '../components';
import { SectionHeading } from '../components/SectionHeading';

export function BlogPostPage({ dark, onToggleDark }: PageProps) {
  const navigate = useNavigate();
  const goHome = () => navigate('/');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const height = doc.scrollHeight - doc.clientHeight;
      const pct = height > 0 ? Math.min(100, Math.max(0, (scrollTop / height) * 100)) : 0;
      setProgress(pct);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="text-slate-900 dark:text-slate-100 selection:bg-[rgba(84,212,248,0.30)]">
      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-50 bg-slate-800">
        <div
          className="h-full"
          style={{ width: `${progress}%`, backgroundColor: PRIMARY }}
        />
      </div>

      <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden">
        <div className="flex grow flex-col">
          <Header
            dark={dark}
            onToggleDark={onToggleDark}
            onGoNotes={() => {}}
            variant="blog"
            onGoHome={goHome}
          />

          <main className="flex flex-1 justify-center py-12 px-6">
            <div className="max-w-[680px] w-full flex flex-col">
              {/* Breadcrumbs */}
              <Breadcrumbs dark={dark} onGoHome={goHome} />

              {/* Article Header */}
              <ArticleHeader dark={dark} />

              {/* Article Content */}
              <ArticleContent dark={dark} />

              {/* Footer Navigation */}
              <ArticleFooter dark={dark} onGoHome={goHome} />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function Breadcrumbs({ dark, onGoHome }: { dark: boolean; onGoHome: () => void }) {
  return (
    <nav className="flex flex-wrap gap-2 mb-8">
      <button
        className={cn(
          'text-sm font-medium hover:text-[var(--primary)] transition-colors',
          dark ? 'text-slate-400' : 'text-slate-500'
        )}
        style={{ '--primary': PRIMARY } as React.CSSProperties}
        onClick={onGoHome}
        type="button"
      >
        Home
      </button>
      <span className="text-slate-400 text-sm">/</span>
      <span
        className={cn('text-sm font-medium', dark ? 'text-slate-400' : 'text-slate-500')}
      >
        Notes
      </span>
      <span className="text-slate-400 text-sm">/</span>
      <span className={cn('text-sm font-medium', dark ? 'text-white' : 'text-slate-900')}>
        Why I'm Building in Public
      </span>
    </nav>
  );
}

function ArticleHeader({ dark }: { dark: boolean }) {
  return (
    <header className="mb-12">
      <h1
        className={cn(
          'tracking-tight text-4xl md:text-5xl font-extrabold leading-tight mb-6',
          dark ? 'text-white' : 'text-slate-900'
        )}
      >
        Why I'm Building in Public
      </h1>
      <div
        className={cn(
          'flex items-center gap-4 text-sm font-medium',
          dark ? 'text-slate-400' : 'text-slate-500'
        )}
      >
        <span className="flex items-center gap-1.5">
          <span className="material-symbols-outlined text-[18px]">calendar_today</span>
          Feb 1, 2026
        </span>
        <span className={dark ? 'text-slate-600' : 'text-slate-700'}>•</span>
        <span className="flex items-center gap-1.5">
          <span className="material-symbols-outlined text-[18px]">schedule</span>4 min
          read
        </span>
      </div>
    </header>
  );
}

function ArticleContent({ dark }: { dark: boolean }) {
  return (
    <article
      className={cn(
        // Typography
        'prose max-w-none',
        // Keep the reading rhythm tighter than the default typography plugin
        'prose-p:my-4 prose-p:leading-relaxed',
        'prose-h2:mt-12 prose-h2:mb-4',
        'prose-img:my-8',
        // Colors
        dark ? 'prose-invert' : 'prose-slate'
      )}
    >
      <p
        className={cn(
          'text-lg mb-8 leading-relaxed font-medium italic border-l-4 pl-6 py-2',
          dark ? 'text-slate-300' : 'text-slate-600'
        )}
        style={{ borderColor: PRIMARY }}
      >
        For a long time, ideas kept coming. And for a long time, I kept finding reasons
        not to start.
      </p>

      <p className={dark ? '' : 'text-slate-700'}>
        Some were reasonable: lack of time, unclear direction, wanting things to be
        "thought through" first. Others were more subtle: waiting for the right moment,
        the right idea, or the right level of confidence.
      </p>

      <p className={dark ? '' : 'text-slate-700'}>
        What eventually pushed me wasn't motivation. It was a constraint. I decided to
        build something small and real, not to ship a product, but to force clarity.
      </p>

      <SectionHeading id="what-surprised-me">What surprised me</SectionHeading>
      <p className={dark ? '' : 'text-slate-700'}>
        What surprised me wasn't the speed of execution. It was how quickly the problem
        became clearer once I had to shape it into an actual flow.
      </p>

      <p className={dark ? '' : 'text-slate-700'}>Ideas are flexible. Systems are not.</p>

      <div className="my-10">
        <img
          className={cn(
            'w-full h-64 object-cover rounded-xl border',
            dark ? 'border-slate-800' : 'border-slate-200'
          )}
          alt="Abstract minimal illustration of connected nodes and systems"
          src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=80"
        />
        <p className="text-center text-xs text-slate-500 mt-4 italic">
          Clarity emerges when you start building.
        </p>
      </div>

      <p className={dark ? '' : 'text-slate-700'}>
        Once I started working through product, UX, and engineering together, most of the
        ambiguity disappeared. What remained was mostly a question of orchestration: how
        things connect, where boundaries should live, and what deserves to exist at all.
      </p>

      <SectionHeading id="the-real-progress-came-from-the-cuts">
        The real progress came from the cuts
      </SectionHeading>
      <p className={dark ? '' : 'text-slate-700'}>
        I stopped trying to solve everything. I picked one goal and let everything else
        wait.
      </p>

      <p className={dark ? '' : 'text-slate-700'}>
        That shift changed how I work. Instead of expanding scope, I started removing it.
        Instead of designing for hypothetical futures, I focused on what needed to exist
        now for the system to make sense.
      </p>

      <p className={dark ? '' : 'text-slate-700'}>
        This is the part of building I find most interesting: not adding features, but
        deciding what not to build.
      </p>

      <SectionHeading id="small-focused-experiments">
        Small, focused experiments
      </SectionHeading>
      <p className={dark ? '' : 'text-slate-700'}>
        Lately, I've been using small, focused experiments to explore these questions: how
        clarity emerges under constraints, how systems evolve when you resist
        over-engineering, and how decisions become easier once the goal is explicit.
      </p>

      <p className={dark ? '' : 'text-slate-700'}>
        I don't know exactly where this leads yet. And that's intentional.
      </p>

      <p className={dark ? '' : 'text-slate-700'}>
        Rather than waiting for something "complete," I want to share the process as it
        happens: the trade-offs, the dead ends, the simplifications, and the occasional
        insights that come from working with real constraints instead of abstractions.
      </p>

      <SectionHeading id="learning-in-public">Learning in public</SectionHeading>
      <p className={dark ? '' : 'text-slate-700'}>
        I'm choosing to do this in public because it changes how I work.
      </p>

      <p className={cn('font-medium mt-0', dark ? '' : 'text-slate-800')}>
        Writing forces clarity. Sharing forces honesty.
      </p>

      <p className={dark ? '' : 'text-slate-700'}>
        Making the work visible keeps ideas from staying abstract for too long and grounds
        them in real constraints. It also creates a light form of accountability, not to
        ship faster, but to think more deliberately.
      </p>

      <p className={dark ? '' : 'text-slate-700'}>
        I'll be writing short notes as I go, sometimes technical, sometimes about product
        or collaboration, always grounded in things I'm actually building or thinking
        through.
      </p>

      <p className={dark ? '' : 'text-slate-700'}>
        If this helps others reflect on similar problems, that's a bonus. The discipline
        itself is the point.
      </p>

      <p className={dark ? '' : 'text-slate-700'}>
        No grand plan. Just one goal at a time.
      </p>
    </article>
  );
}

function ArticleFooter({ dark, onGoHome }: { dark: boolean; onGoHome: () => void }) {
  return (
    <footer
      className={cn(
        'mt-20 pt-10 border-t pb-20',
        dark ? 'border-slate-800' : 'border-slate-200'
      )}
    >
      <div className="flex flex-col items-center text-center gap-6">
        <p
          className={cn('text-sm mono-text', dark ? 'text-slate-500' : 'text-slate-400')}
        >
          More notes coming soon. One goal at a time.
        </p>

        <button
          onClick={onGoHome}
          className="group flex items-center gap-2 text-sm font-medium transition-colors"
          style={{ color: PRIMARY }}
          type="button"
        >
          <span className="transition-transform group-hover:-translate-x-1">←</span>
          Back to home
        </button>
      </div>
    </footer>
  );
}
